import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Store, MapPin, Phone, Clock, Edit2, Save, X, Plus, Trash2 } from 'lucide-react';
import './PickupManager.css';

function PickupManager() {
  const [pickupLocations, setPickupLocations] = useState([]);
  const [editingLocation, setEditingLocation] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    hours: '',
    estimatedReady: '',
    icon: '🏪'
  });

  useEffect(() => {
    loadPickupLocations();
  }, []);

  const loadPickupLocations = async () => {
    try {
      const pickupSnapshot = await getDocs(collection(db, 'pickupLocations'));
      const locations = pickupSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPickupLocations(locations);
    } catch (error) {
      console.error('Error loading pickup locations:', error);
    }
  };

  const savePickupLocation = async () => {
    try {
      const locationData = {
        name: formData.name,
        address: formData.address,
        phone: formData.phone,
        hours: formData.hours,
        estimatedReady: formData.estimatedReady,
        icon: formData.icon,
        active: true,
        updatedAt: new Date()
      };

      if (editingLocation && editingLocation.id) {
        await updateDoc(doc(db, 'pickupLocations', editingLocation.id), locationData);
      } else {
        await setDoc(doc(db, 'pickupLocations', `location_${Date.now()}`), locationData);
      }

      resetForm();
      loadPickupLocations();
    } catch (error) {
      console.error('Error saving pickup location:', error);
    }
  };

  const startEditing = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address,
      phone: location.phone,
      hours: location.hours,
      estimatedReady: location.estimatedReady,
      icon: location.icon
    });
  };

  const resetForm = () => {
    setEditingLocation(false);
    setFormData({
      name: '',
      address: '',
      phone: '',
      hours: '',
      estimatedReady: '',
      icon: '🏪'
    });
  };

  const deletePickupLocation = async (locationId) => {
    try {
      await updateDoc(doc(db, 'pickupLocations', locationId), { active: false });
      loadPickupLocations();
    } catch (error) {
      console.error('Error deleting pickup location:', error);
    }
  };

  const icons = ['🏪', '🏬', '🏭', '🏢', '🏣', '🏤', '🏥', '🏦'];

  return (
    <div className="pickup-manager">
      <div className="pickup-header">
        <h2><Store size={24} /> Pickup Location Management</h2>
        <p>Manage local pickup locations for customers</p>
      </div>

      {/* Pickup Locations */}
      <div className="pickup-locations-section">
        <div className="section-header">
          <h3><MapPin size={20} /> Pickup Locations</h3>
          <button 
            className="add-location-btn" 
            onClick={() => setEditingLocation(true)}
          >
            <Plus size={16} />
            Add Location
          </button>
        </div>

        {/* Add/Edit Form */}
        {editingLocation && (
          <div className="pickup-form">
            <h4>{editingLocation.id ? 'Edit Pickup Location' : 'Add Pickup Location'}</h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Location Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Brooklyn Store"
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="123 Fulton Street, Brooklyn, NY 11201"
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="(718) 123-4567"
                />
              </div>

              <div className="form-group">
                <label>Hours</label>
                <input
                  type="text"
                  value={formData.hours}
                  onChange={(e) => setFormData({...formData, hours: e.target.value})}
                  placeholder="Mon-Sat: 10AM-7PM, Sun: 11AM-5PM"
                />
              </div>

              <div className="form-group">
                <label>Ready Time</label>
                <input
                  type="text"
                  value={formData.estimatedReady}
                  onChange={(e) => setFormData({...formData, estimatedReady: e.target.value})}
                  placeholder="2-4 hours"
                />
              </div>

              <div className="form-group">
                <label>Icon</label>
                <div className="icon-selector">
                  {icons.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      className={`icon-option ${formData.icon === icon ? 'selected' : ''}`}
                      onClick={() => setFormData({...formData, icon})}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button onClick={resetForm} className="cancel-btn">
                <X size={16} />
                Cancel
              </button>
              <button onClick={savePickupLocation} className="save-btn">
                <Save size={16} />
                {editingLocation.id ? 'Update' : 'Add'} Location
              </button>
            </div>
          </div>
        )}

        {/* Locations List */}
        <div className="locations-list">
          {pickupLocations.filter(location => location.active).map(location => (
            <div key={location.id} className="location-card">
              <div className="location-info">
                <div className="location-header">
                  <div className="location-icon">{location.icon}</div>
                  <h4>{location.name}</h4>
                </div>
                <div className="location-details">
                  <div className="detail-item">
                    <MapPin size={14} />
                    <span>{location.address}</span>
                  </div>
                  <div className="detail-item">
                    <Phone size={14} />
                    <span>{location.phone}</span>
                  </div>
                  <div className="detail-item">
                    <Clock size={14} />
                    <span>{location.hours}</span>
                  </div>
                  <div className="detail-item">
                    <span className="ready-time">Ready: {location.estimatedReady}</span>
                  </div>
                </div>
              </div>
              <div className="location-actions">
                <button onClick={() => startEditing(location)} className="edit-btn">
                  <Edit2 size={16} />
                </button>
                <button onClick={() => deletePickupLocation(location.id)} className="delete-btn">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PickupManager;
